import React, { useState, useEffect } from "react";
import Banner from "./homePageComponents/Banner";
import CategoryFeatures from "./homePageComponents/CategoryFeatures";
import WelcomeSection from "./homePageComponents/WelcomeSection";
import HomeBookSection from "./homePageComponents/HomeBookSection";
import FeaturedAuthors from "./homePageComponents/FeaturedAuthors";
import BookManager from "../../services/home/BookManager";
import AuthorManager from "../../services/home/AuthorManager";
import BookHighlightManager from "../../services/home/BookHighlightManager";
import BookOfTheMonth from "./homePageComponents/BookOfTheMonth";
import HowItWorks from "./homePageComponents/HowItWorks";
import TestimonialsSection from "./homePageComponents/TestimonialsSection";
import CategoriesSection from "./homePageComponents/CategoriesSection";
import { useNavigate } from "react-router-dom";
import StaggeredBookBanner from "./homePageComponents/StaggeredBookBanner";
import BlogSection from "./homePageComponents/BlogSection";
import { Link } from "react-router-dom";

export default function HomePage() {
  const [recommendedBooks, setRecommendedBooks] = useState([]);
  const [trendingBooks, setTrendingBooks] = useState([]);
  const [justLaunchedBooks, setJustLaunchedBooks] = useState([]);
  const [bestReadsBooks, setbestReadsBooks] = useState([]);
  const [topReadsBooks, setTopReadsBooks] = useState([]);
  const [rentedBooks, setRentedBooks] = useState([]);
  const [bookOfTheMonth, setBookOfTheMonth] = useState(null);
  const [bookOfMonthDetails, setBookOfMonthDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchHighlightedBooks = async () => {
    try {
      // 1️⃣ Fetch highlights, authors, and rented books in parallel
      const [
        trendingHighlights,
        justLaunchedHighlights,
        bestReadsHighlights,
        authorsRes,
        rentedRes,
      ] = await Promise.all([
        BookHighlightManager.getTrendingBooks(),
        BookHighlightManager.getJustLaunchedBooks(),
        BookHighlightManager.getBestReadsBooks(),
        AuthorManager.getAllAuthors(),
        BookManager.getRentedBooks(20, null),
      ]);
  
      // 2️⃣ Build authorMap before using it anywhere
      const authors =
        authorsRes.responseCode === 200 && authorsRes.errorCode === null
          ? authorsRes.responseData || []
          : [];
  
      const authorMap = {};
      authors.forEach((author) => {
        authorMap[author.authorId] = author.authorName;
      });
  
      // Fetch Recommendations
      let recBooksMapped = [];
      try {
        const recBooks = await BookManager.getRecommendedBooks();
        recBooksMapped = recBooks.map((b) => ({
          id: b.bookId,
          image: b.bookImages?.[0] || "defaultImage.jpg",
          title: b.bookName,
          author: authorMap[b.authorId] || "Unknown",
          isbn: b.bookIsbn10,
          rating: b.averageRating,
          inStock: b.bookStockStatus,
        }));
      } catch (err) {
        console.error("Error mapping recommended books", err);
      }

      // 3️⃣ Map “Top Rated” using authorMap
      const topRatedList = await BookManager.getTopRatedBooks(10, null);
      const topReadsMapped = topRatedList.map((b) => ({
        id: b.bookId,
        image: b.bookImages?.[0] || "defaultImage.jpg",
        title: b.bookName,
        author: authorMap[b.authorId] || "Unknown",
        isbn: b.bookIsbn10,
        rating: b.averageRating,
        inStock: b.bookStockStatus,
      }));
  
      // 4️⃣ Helper to map highlight directly (no extra fetch)
      const mapToBookCard = (book) => ({
        id: book.bookId,
        image: book.bookImages?.[0] || "defaultImage.jpg",
        title: book.bookName,
        author: authorMap[book.authorId] || "Unknown",
        isbn: book.bookIsbn10,
        rating: book.averageRating,
        inStock: book.bookStockStatus,
      });

      // 5️⃣ Map highlights and rented books (no need for Promise.all)
      const trendingBooksMapped = trendingHighlights.map(mapToBookCard);
      const justLaunchedBooksMapped = justLaunchedHighlights.map(mapToBookCard);
      const bestReadsMapped = bestReadsHighlights.map(mapToBookCard);
      const rentedBooksMapped = (rentedRes || []).map(mapToBookCard);

      // 6️⃣ Push into state
      setRecommendedBooks(recBooksMapped);
      setTrendingBooks(trendingBooksMapped);
      setJustLaunchedBooks(justLaunchedBooksMapped);
      setbestReadsBooks(bestReadsMapped);
      setTopReadsBooks(topReadsMapped);
      setRentedBooks(rentedBooksMapped);
    } catch (err) {
      console.error("Error fetching highlighted books:", err);
      setError("Failed to load highlighted books.");
    }
  };


  const fetchBookOfTheMonth = async () => {
    try {
      const botm = await BookHighlightManager.getBookOfTheMonth();
      setBookOfTheMonth(botm);
      if (botm?.bookId) {
        const fullBook = await BookManager.getBookById(botm.bookId);
        setBookOfMonthDetails(fullBook);
      }
    } catch (err) {
      console.error("Error fetching book of the month:", err);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      await Promise.all([fetchHighlightedBooks(), fetchBookOfTheMonth()]);
      setLoading(false);
    };

    fetchAll();
  }, []);

  return (
    <div>
      <Banner />

      <CategoryFeatures className="sm:-mt-6 mt-4" />

      {/* <WelcomeSection />
      <div className="mb-16" /> */}

      <section className="w-full bg-white py-6 overflow-x-hidden">
        <div className="max-w-screen-xl mx-auto px-4 md:px-10 space-y-2">
          
          {recommendedBooks.length > 0 && (
            <HomeBookSection
              title="Recommended For You"
              books={recommendedBooks}
              loading={loading}
            />
          )}

          <HomeBookSection
            title="Trending Books"
            books={trendingBooks}
            onSeeAll={() => navigate("/trending-books")}
            loading={loading}
          />

          <CategoriesSection />

          <HomeBookSection
            title="Just Launched"
            books={justLaunchedBooks}
            onSeeAll={() => navigate("/justlaunched-books")}
            loading={loading}
          />

          {/* Membership Banner Section */}
          <section className="w-full py-8 md:py-12">
            <Link to="/membershipPlans" className="block w-full cursor-pointer hover:scale-[1.01] transition-transform duration-300">
              <img
                src="/images/banner mid.jpg"
                alt="Membership plans banner"
                className="w-full h-auto rounded-2xl shadow-lg object-cover"
                loading="lazy"
              />
            </Link>
          </section>

          {(loading || rentedBooks.length > 0) && (
            <HomeBookSection
              title="Most Rentals"
              books={rentedBooks}
              onSeeAll={() => navigate("/explore-books")}
              loading={loading}
            />
          )}

          <HomeBookSection
            title="Best Reads"
            books={bestReadsBooks}
            onSeeAll={() => navigate("/bestreads-books")}
            loading={loading}
          />

          <HomeBookSection
            title="Top Rated"
            books={topReadsBooks}
            onSeeAll={() => navigate("/top-rated-books")}
            loading={loading}
          />

          <FeaturedAuthors />
        </div>
      </section>
      <StaggeredBookBanner />

      <div className="mt-4">
        <BookOfTheMonth book={bookOfMonthDetails} />
      </div>

      <HowItWorks />

      <TestimonialsSection />
      
      <BlogSection />
    </div>
  );
}
